import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import type { ReactTable, RowData } from "@tanstack/react-table";
import { useMemo } from "react";

import type { DataTableFeatures } from "../../features";

/**
 * The drag-reorder half of the column menu: which columns are listed, in what
 * order, which of them may move, and what a completed drag writes back.
 *
 * A completed drag writes the FULL column order, fixed columns included, so the
 * consumer holding that state never has to reason about the gaps a partial list
 * would leave.
 */
function useColumnReorder(table: ReactTable<DataTableFeatures, RowData>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const columns = table.getAllLeafColumns();
  const { columnOrder } = table.state;

  const orderedColumns = useMemo(() => {
    const position = new Map(columnOrder.map((id, index) => [id, index]));

    return [...columns].sort(
      (a, b) =>
        (position.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (position.get(b.id) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [columns, columnOrder]);

  const reorderableIds = orderedColumns
    .filter((column) => column.getCanHide())
    .map((column) => column.id);

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;

    const fromIndex = reorderableIds.indexOf(String(active.id));
    const toIndex = reorderableIds.indexOf(String(over.id));
    if (fromIndex === -1 || toIndex === -1) return;

    const moved = arrayMove(reorderableIds, fromIndex, toIndex);
    const nextOrder = orderedColumns.map((column) =>
      column.getCanHide() ? (moved.shift() ?? column.id) : column.id
    );

    table.setColumnOrder(nextOrder);
  }

  return { sensors, orderedColumns, reorderableIds, handleDragEnd };
}

export { useColumnReorder };
